import { useCallback, useEffect, useState } from "react";
import { QuranResponseType, QuranTranslationType } from "../types";
import { table } from "node:console";

export interface UseIndexedDBResult<T> {
  getRecord: (tableName: string, id: number) => Promise<T | undefined>;
  getAllRecords: (
    tableName: string,
    range?: IDBKeyRange,
  ) => Promise<T[] | undefined>;
  saveRecord: (tableName: string, value: T) => Promise<IDBValidKey | null>;
  saveRecords: (
    tableName: string,
    values: T[] | { [key: string]: T[] },
  ) => Promise<T[] | { [key: string]: T[] } | undefined>;
  updateRecord: (params: {
    tableName: string;
    id: string;
    newItem: any;
  }) => void;
  deleteRecord: (tableName: string, id: string) => string | null;
  deleteAllRecords: (tableName: string) => void;
  isDBConnecting: boolean;
}

export const useIndexedDB = <T,>(
  databaseName: string,
  tableNames: string[],
  keypath: string = "id",
  version: number = 1,
): UseIndexedDBResult<T> => {
  const [db, setDB] = useState<IDBDatabase | null>(null);
  const [isDBConnecting, setIsDBConnecting] = useState<boolean>(true);

  useEffect(() => {
    const initDB = () => {
      const request = indexedDB.open(databaseName, version);

      // Handle database upgrade
      request.onupgradeneeded = () => {
        const database = request.result;
        tableNames.forEach((tableName) => {
          if (!database.objectStoreNames.contains(tableName)) {
            console.log("creating table: ", tableName);
            database.createObjectStore(tableName, {
              autoIncrement: true,
              keyPath: keypath,
            });
          }
        });
      };

      request.onsuccess = () => {
        setDB(request.result);
        setIsDBConnecting(false);
      };

      request.onerror = () => {
        console.error("Error initializing IndexedDB:", request.error);
        setIsDBConnecting(false);
      };
    };

    if (!db) {
      initDB();
    }
  }, [databaseName, tableNames, db, version]);

  const getTransaction = (tableName: string, mode: IDBTransactionMode) => {
    if (!db) throw new Error("Database is not initialized");

    try {
      const transaction = db.transaction(tableName, mode);
      if (!transaction) return undefined;

      return transaction.objectStore(tableName);
    } catch (e) {
      return undefined;
    }
  };

  const getRecord = useCallback(
    (tableName: string, id: number): Promise<T | undefined> => {
      return new Promise((resolve, reject) => {
        try {
          const store = getTransaction(tableName, "readonly");
          let request: IDBRequest<{ id: number; page: T }>;
          if (store) {
            request = store.get(id);
            request.onsuccess = () => {
              if (request.result && "page" in request.result)
                resolve(request.result.page);
              else resolve(request.result);
            };
            request.onerror = () => reject(request.error);
          } else resolve(undefined);
        } catch (error) {
          reject(error);
        }
      });
    },

    //eslint-disable-next-line
    [db],
  );

  const getAllRecords = (
    tableName: string,
    range?: IDBKeyRange,
  ): Promise<T[] | undefined> => {
    return new Promise((resolve, reject) => {
      try {
        const store = getTransaction(tableName, "readonly");
        let request: IDBRequest<T[]>;
        if (store) {
          if (range) request = store.getAll(range);
          else request = store.getAll();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        } else resolve(undefined);
      } catch (error) {
        reject(error);
      }
    });
  };

  const saveRecord = (
    tableName: string,
    value: T,
  ): Promise<IDBValidKey | null> => {
    return new Promise((resolve, reject) => {
      try {
        const store = getTransaction(tableName, "readwrite");
        if (store) {
          const request = store.put(value);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const saveRecords = async (
    tableName: string,
    values: T[] | { [key: string]: T[] },
  ): Promise<T[] | { [key: string]: T[] } | undefined> => {
    try {
      const store = getTransaction(tableName, "readwrite");
      if (store) {
        if (Array.isArray(values)) values.forEach((value) => store.put(value));
        else {
          //TODO....This logic should be outside of useIndexedDB
          Object.keys(values).forEach((key) => {
            let page: { id: number; page: T[] } = { id: 0, page: [] };
            let v: any = values[key][0]!!;
            page = {
              id: v.words[0].page,
              page: values[key],
            };
            store.put(page);
          });
        }
      }

      return getAllRecords(tableName);
    } catch (error) {
      throw new Error("Bulk insert failed: " + error);
    }
  };

  const updateRecord = ({
    tableName,
    id,
    newItem,
  }: {
    tableName: string;
    id: string;
    newItem: T;
  }) => {
    try {
      const store = getTransaction(tableName, "readwrite");
      let request: IDBRequest<any>;
      if (store) {
        request = store.get(id);
        request.onsuccess = () => {
          const data = request.result;
          const updatedItem = data ? { ...data, ...newItem } : { id, newItem };
          store.put(updatedItem);
        };
      }
    } catch (error) {
      console.error("Update value failed: ", error);
    }
  };

  const deleteRecord = (tableName: string, id: string): string | null => {
    try {
      const store = getTransaction(tableName, "readwrite");
      if (store) store.delete(id);
      return id;
    } catch (error) {
      console.error("Delete value failed: ", error);
      return null;
    }
  };

  const deleteAllRecords = (tableName: string) => {
    try {
      const store = getTransaction(tableName, "readwrite");
      if (store) store.clear();
    } catch (error) {
      console.error("Delete all values failed: ", error);
    }
  };

  return {
    updateRecord,
    getRecord,
    getAllRecords,
    saveRecord,
    saveRecords,
    deleteRecord,
    deleteAllRecords,
    isDBConnecting,
  };
};
